// src/App.js - COMPLETE FIXED VERSION
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import LoginPage from "./pages/LoginPage";
import StudentDashboard from "./pages/StudentDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import StartupProfile from "./pages/StartupProfile";
import FormBuilder from "./pages/FormBuilder";
import FormSubmission from "./pages/FormSubmission";
import Scoreboard from "./pages/Scoreboard";
import NotFoundPage from "./pages/NotFoundPage";
import AdminLayout from "./pages/AdminLayout";
import StartupsPage from "./pages/Startup/StartupListing";
import NewStartupPage from "./pages/Startup/CreateStartup";
import StartupDetailPage from "./pages/Startup/StartupDetail";
import CompetitionForm from "./pages/Startup/CompetitionForm";
import CompetitionListing from "./pages/Startup/CompetitionTable";
import CreateFormFromJSON from "./pages/CreateFormFromJSON";
import UsersListPage from "./pages/UserListPage";
import MyStartupPage from "./pages/Startup/MyStartup";
import AdminTeams from "./pages/admin/AdminTeamPage";
import StudentTeams from "./pages/student/StudentTeamsPage";
import StartupDashboard from "./pages/Startup/StartupDashboard";
import FormListing from "./pages/FormListing";
import FormTypeSelector from "./pages/FormTypeSelector";
import EvaluationFormBuilder from "./pages/EvaluationFormBuilder";
import FeedbackFormBuilder from "./pages/FeedbackFormBuilder";
import InputDetailFormBuilder from "./pages/InputDetailFormBuilder";

// Learning Module Imports
import LearningPathsPage from "./pages/student/LearningPathsPage";
import LearningPathDetailPage from "./pages/student/LearningPathDetailPage";
import LearningProgressPage from "./pages/student/LearningProgressPage";
import LevelTestPage from "./pages/student/LevelTestPage";
import RankingPage from "./pages/student/RankingPage";
import AssignCreditsPage from "./pages/admin/AssignCreditsPage";
import CreditHistoryPage from "./pages/student/CreditHistoryPage";
import AdminLearningPathsPage from "./pages/admin/AdminLearningPathsPage";
import CreateLearningPathPage from "./pages/admin/CreateLearningPathPage";
import EditLearningPathPage from "./pages/admin/EditLearningPathPage";
import LearningPathScoreboardPage from "./pages/admin/LearningPathScoreboardPage";
import UserProfilePage from "./pages/student/UserProfilePage";
import OnboardingGate from "./pages/OnboardingGate";
import AssessmentTestPage from "./pages/AssessmentTestPage";
import DashboardUnlockPage from "./pages/DashboardUnlockPage";

// Event Management Imports
import EventsManagement from "./pages/admin/EventsManagement";
import CreateEvent from "./pages/admin/CreateEvent";
import EventDetails from "./pages/admin/EventDetails";
import EventsListing from "./pages/student/EventsListing";
import MyEvents from "./pages/student/MyEvents";
import AdminEventsV2List from "./pages/admin/AdminEventsV2List";
import AdminEventV2Detail from "./pages/admin/AdminEventV2Detail";
import AdminEventV2Create from "./pages/admin/AdminEventV2Create";
import EventDetailV2 from "./pages/student/EventDetailV2";
import EventQuizPage from "./pages/student/EventQuizPage";
import EventTimelinePage from "./pages/student/EventTimelinePage";
import AdminQuizPage from "./pages/admin/AdminQuizPage";
import EventRegisterV2 from "./pages/student/EventRegisterV2";
import SettingsPage from "./pages/SettingsPage";

function App() {
    return (
        <AuthProvider>
            <Router>
                <Routes>
                    {/* ===== PUBLIC ROUTES ===== */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/" element={<Navigate to="/login" replace />} />

                    {/* ===== ONBOARDING FLOW ===== */}
                    <Route
                        path="/onboarding"
                        element={
                            <ProtectedRoute allowedRoles={["student", "startup", "faculty", "admin"]}>
                                <OnboardingGate />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/assessment"
                        element={
                            <ProtectedRoute allowedRoles={["student", "startup", "faculty", "admin"]}>
                                <AssessmentTestPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/unlock"
                        element={
                            <ProtectedRoute allowedRoles={["student", "startup", "faculty", "admin"]}>
                                <DashboardUnlockPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* ===== STUDENT ROUTES ===== */}
                    <Route
                        path="/student"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <StudentDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute allowedRoles={["student", "startup", "faculty", "admin"]}>
                                <SettingsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/profile"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <UserProfilePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/teams"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <StudentTeams />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/learning/paths"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <LearningPathsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/learning/paths/:pathId"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <LearningPathDetailPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/learning/progress"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <LearningProgressPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/learning/test/:pathId/:levelId"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <LevelTestPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/ranking"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <RankingPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/credits/history"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <CreditHistoryPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/startups"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <StartupsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/startup/new"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <NewStartupPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/my-startup"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <MyStartupPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/events"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <EventsListing />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/my-events"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <MyEvents />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/events-v2/:eventId"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <EventDetailV2 />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/events-v2/:eventId/quiz"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <EventQuizPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/events-v2/:eventId/timeline"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <EventTimelinePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/events-v2/:eventId/register"
                        element={
                            <ProtectedRoute allowedRoles={["student"]}>
                                <EventRegisterV2 />
                            </ProtectedRoute>
                        }
                    />

                    {/* ===== ADMIN ROUTES ===== */}
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <AdminDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/users"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <UsersListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/teams"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <AdminTeams />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/form-builder"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <FormBuilder />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/forms"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <FormListing />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/forms/create"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <FormTypeSelector />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/forms/evaluation/:formId"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <EvaluationFormBuilder />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/forms/feedback/:formId"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <FeedbackFormBuilder />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/forms/input-detail/:formId"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <InputDetailFormBuilder />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/forms"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <FormListing />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/scoreboard"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <Scoreboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/startups"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <StartupsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/learning-paths"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <AdminLearningPathsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/learning-paths/create"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <CreateLearningPathPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/learning-paths/edit/:pathId"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <EditLearningPathPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/learning-paths/scoreboard"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <LearningPathScoreboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/learning-paths/scoreboard/:pathId"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <LearningPathScoreboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/credits/assign"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <AssignCreditsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/events"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <EventsManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/events-v2"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <AdminEventsV2List />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/events-v2/create"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <AdminEventV2Create />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/events-v2/:eventId"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <AdminEventV2Detail />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/events/create"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <CreateEvent />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/quiz"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <AdminQuizPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/events/:eventId"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <EventDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/events/:eventId/edit"
                        element={
                            <ProtectedRoute allowedRoles={["admin"]}>
                                <CreateEvent />
                            </ProtectedRoute>
                        }
                    />

                    {/* ===== STARTUP ROUTES ===== */}
                    <Route
                        path="/startup"
                        element={
                            <ProtectedRoute allowedRoles={["startup"]}>
                                <StartupDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/startup/profile"
                        element={
                            <ProtectedRoute allowedRoles={["startup"]}>
                                <StartupProfile />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/startup/:id"
                        element={
                            <ProtectedRoute allowedRoles={["student", "admin", "startup"]}>
                                <StartupDetailPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/competition"
                        element={
                            <ProtectedRoute allowedRoles={["startup"]}>
                                <CompetitionListing />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/competition/:competitionId/apply"
                        element={
                            <ProtectedRoute allowedRoles={["startup"]}>
                                <CompetitionForm />
                            </ProtectedRoute>
                        }
                    />

                    {/* ===== SHARED ROUTES ===== */}
                    <Route
                        path="/form/:formId"
                        element={
                            <ProtectedRoute allowedRoles={["student", "admin", "startup", "faculty"]}>
                                <FormSubmission />
                            </ProtectedRoute>
                        }
                    />

                    {/* ===== CATCH-ALL ROUTES ===== */}
                    <Route path="/404" element={<NotFoundPage />} />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
            </Router>
        </AuthProvider>
    );
}

export default App;
